import { ArrowUpTrayIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ClientButton } from '@/components/UI/Button';
import { authToken } from '@/renderer/utils/useMe';

const API_BASE =
  (import.meta.env.VITE_PUBLIC_ENV__API_URL as string) ||
  (typeof location !== 'undefined' ? location.origin : '');

// The client normalizes to a small square before upload, so the bytes we send
// are tiny regardless of the source file. We still guard the picked file's size
// up front to reject an absurd input before decoding it.
const MAX_SOURCE_BYTES = 15 * 1024 * 1024;
const THUMB_SIZE = 256;

/** Read the Bearer token the same way renderer/utils/api.ts does (JSON-quoted). */
function bearerToken(): string | undefined {
  const raw = typeof window !== 'undefined' ? authToken() : undefined;
  if (typeof raw === 'string' && raw.startsWith('"')) {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }
  return raw ?? undefined;
}

/**
 * Downscale + center-crop the chosen image to a square THUMB_SIZE thumbnail via
 * a canvas, returning a compact Blob. This is our "square thumbnail" step (the
 * server has no image library), and it also caps the uploaded byte size. Prefers
 * WebP, falling back to JPEG where canvas WebP encoding isn't available.
 */
async function squareThumbnail(file: File): Promise<{ blob: Blob; type: string }> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Could not read that image.'));
      el.src = url;
    });

    const side = Math.min(img.naturalWidth, img.naturalHeight);
    if (!side) {
      throw new Error('Could not read that image.');
    }
    const sx = (img.naturalWidth - side) / 2;
    const sy = (img.naturalHeight - side) / 2;

    const canvas = document.createElement('canvas');
    canvas.width = THUMB_SIZE;
    canvas.height = THUMB_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not process that image.');
    }
    ctx.drawImage(img, sx, sy, side, side, 0, 0, THUMB_SIZE, THUMB_SIZE);

    const toBlob = (type: string) =>
      new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), type, 0.9));

    const webp = await toBlob('image/webp');
    if (webp && webp.type === 'image/webp') {
      return { blob: webp, type: 'image/webp' };
    }
    const jpeg = await toBlob('image/jpeg');
    if (jpeg) {
      return { blob: jpeg, type: 'image/jpeg' };
    }
    throw new Error('Could not process that image.');
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Staff/admin profile-photo control (#1999). Shows the current avatar (photo or
 * initials), and lets the user upload/replace or remove a photo. Uploads a raw
 * (normalized, square) image body to POST /user/avatar, matching the support-chat
 * image upload pattern (Bearer token so it works in the cookieless native app).
 * Regular users never see this row (gated at the call site in +Page.tsx).
 */
export default function ProfilePhotoField({
  user,
  setUser,
}: {
  user: API.User;
  setUser: (newUser: API.User | null) => void;
}) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPrivileged = user.role === 'admin' || user.role === 'staff';
  const avatarUrl = user.avatarUrl || undefined;

  const openPicker = () => {
    setError(null);
    fileRef.current?.click();
  };

  const onPick = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset the input so picking the same file again re-fires change.
    event.target.value = '';
    if (!file) {
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }
    if (file.size > MAX_SOURCE_BYTES) {
      setError('That image is too large. Please choose one under 15MB.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const { blob, type } = await squareThumbnail(file);
      const bearer = bearerToken();
      const res = await fetch(`${API_BASE}/api/v1/user/avatar`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': type,
          ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
        },
        body: blob,
      });
      if (!res.ok) {
        const msg = await res
          .json()
          .then((j) => (j as { message?: string })?.message)
          .catch(() => null);
        throw new Error(msg || `Upload failed (${res.status})`);
      }
      const updated = ((await res.json()) as { data?: API.User })?.data;
      if (updated) {
        setUser(updated);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      const bearer = bearerToken();
      const res = await fetch(`${API_BASE}/api/v1/user/avatar`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}) },
      });
      if (!res.ok) {
        const msg = await res
          .json()
          .then((j) => (j as { message?: string })?.message)
          .catch(() => null);
        throw new Error(msg || `Remove failed (${res.status})`);
      }
      const updated = ((await res.json()) as { data?: API.User })?.data;
      if (updated) {
        setUser(updated);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Remove failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4">
        {/* Live preview: the uploaded photo, or the initials fallback tile. */}
        <div
          className={twMerge(
            'relative flex h-16 w-16 shrink-0 select-none items-center justify-center overflow-hidden rounded-sm text-white',
            isPrivileged ? 'bg-emerald-700 dark:bg-emerald-500' : 'bg-zinc-400 dark:bg-zinc-600',
          )}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="Your profile photo"
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <span className="font-medium text-xl uppercase">
              {user.firstName?.charAt(0)}
              {user.lastName?.charAt(0)}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <ClientButton
            type="button"
            variant="outline"
            size="sm"
            onClick={openPicker}
            disabled={busy}
            loading={busy}
            className="inline-flex items-center gap-1.5"
          >
            <ArrowUpTrayIcon className="size-4" aria-hidden="true" />
            {avatarUrl ? 'Replace photo' : 'Upload photo'}
          </ClientButton>
          {avatarUrl && (
            <ClientButton
              type="button"
              variant="link"
              size="baseline"
              onClick={remove}
              disabled={busy}
              className="inline-flex items-center gap-1 p-0 font-medium text-zinc-500 no-underline hover:text-red-600 hover:no-underline dark:text-zinc-400 dark:hover:text-red-400"
            >
              <XMarkIcon className="size-4" aria-hidden="true" />
              Remove
            </ClientButton>
          )}
        </div>
      </div>

      <p className="text-xs text-zinc-500 leading-5 dark:text-zinc-400">
        JPEG, PNG, or WebP. Cropped to a square. Shown next to your name across the app.
      </p>

      {error && <p className="text-red-600 text-xs dark:text-red-400">{error}</p>}

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={onPick}
      />
    </div>
  );
}
